import type {PerkIntegrationSpec, PerkIntegrationStruct} from '@cohort/shared/apps/app';
import {urlTransformer} from '@cohort/shared/utils/validators';
import {urlValidator} from '@cohort/shared/utils/validators';
import {z} from 'zod';

const CohortPromoCodeTypeSchema = z.enum(['universal', 'unique']);

const RedirectionUrlSchema = z
  .string()
  .refine(urlValidator, 'errorUrl')
  .transform(url => urlTransformer(url));

export const CohortPromoCodeSchema = z
  .string()
  .regex(
    /^[A-Za-z0-9_-]+$/,
    'Promo code must only contain letters, numbers, underscores, and hyphens'
  );
export type CohortPromoCode = z.infer<typeof CohortPromoCodeSchema>;

export const ActivatedCohortPromoCodeAccessDataSchema = z.object({
  code: CohortPromoCodeSchema,
});
export type ActivatedCohortPromoCodeAccessData = z.infer<
  typeof ActivatedCohortPromoCodeAccessDataSchema
>;
export const CohortPromoCodeAccessDataSchema = z.union([
  ActivatedCohortPromoCodeAccessDataSchema,
  z.object({}),
]);
export type CohortPromoCodeAccessData = z.infer<typeof CohortPromoCodeAccessDataSchema>;

export const CohortPromoCodeUniversalConfigSchema = z.object({
  type: z.literal('universal'),
  code: CohortPromoCodeSchema,
  redirectionUrl: RedirectionUrlSchema,
});

export const CohortPromoCodeUniqueConfigSchema = z.object({
  type: z.literal('unique'),
  available: z.array(CohortPromoCodeSchema),
  used: z.array(CohortPromoCodeSchema),
  redirectionUrl: RedirectionUrlSchema,
});

export const CohortPromoCodeConfigSchema = z.discriminatedUnion('type', [
  CohortPromoCodeUniversalConfigSchema,
  CohortPromoCodeUniqueConfigSchema,
]);
export type CohortPromoCodeConfig = z.infer<typeof CohortPromoCodeConfigSchema>;

export const CohortPromoCodeExposedConfigSchema = z.object({
  type: CohortPromoCodeTypeSchema,
  redirectionUrl: RedirectionUrlSchema,
});
export type CohortPromoCodeExposedConfig = z.infer<typeof CohortPromoCodeExposedConfigSchema>;

export const CohortPromoCodeStateSchema = z.object({});
export type CohortPromoCodeState = z.infer<typeof CohortPromoCodeStateSchema>;

export const CohortPromoCodeUsageDataSchema = z.object({
  type: CohortPromoCodeTypeSchema,
  code: CohortPromoCodeSchema,
  redirectionUrl: RedirectionUrlSchema,
});
export type CohortPromoCodeUsageData = z.infer<typeof CohortPromoCodeUsageDataSchema>;

export const ExposedCohortPromoCodeUsageDataSchema = z.object({
  type: CohortPromoCodeTypeSchema,
  code: z.string(),
  redirectionUrl: RedirectionUrlSchema,
});
export type ExposedCohortPromoCodeUsageData = z.infer<typeof ExposedCohortPromoCodeUsageDataSchema>;

export type CohortPromoCodePerkIntegrationStruct = PerkIntegrationStruct<
  'cohort.promo-code',
  CohortPromoCodeConfig,
  CohortPromoCodeExposedConfig,
  CohortPromoCodeState,
  CohortPromoCodeAccessData,
  null,
  CohortPromoCodeUsageData,
  ExposedCohortPromoCodeUsageData,
  ExposedCohortPromoCodeUsageDataForExport
>;

const ExposedCohortPromoCodeUsageDataForExportSchema = ExposedCohortPromoCodeUsageDataSchema;
export type ExposedCohortPromoCodeUsageDataForExport = z.infer<
  typeof ExposedCohortPromoCodeUsageDataForExportSchema
>;

export const CohortPromoCodePerkIntegrationSpec: PerkIntegrationSpec<CohortPromoCodePerkIntegrationStruct> =
  {
    id: 'cohort.promo-code' as const,
    configSchema: CohortPromoCodeConfigSchema,
    exposedConfigSchema: CohortPromoCodeExposedConfigSchema,
    stateSchema: CohortPromoCodeStateSchema,
    accessDataSchema: CohortPromoCodeAccessDataSchema,
    usageInputDataSchema: z.null(),
    internalUsageDataSchema: CohortPromoCodeUsageDataSchema,
    exposedUsageDataSchema: ExposedCohortPromoCodeUsageDataSchema,
    exposedUsageDataForExportSchema: ExposedCohortPromoCodeUsageDataForExportSchema,
    defaultMaxUsagesPerToken: null,
    requiresMerchantConnection: false,
    localizedConfig: true,
  };
